import React, { lazy } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Play } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { WavyDivider } from '../components/WavyDivider';
import { BlogCard } from '../components/BlogCard';
import { TestimonialCard } from '../components/TestimonialCard';
import { blogPosts } from '../data/blog';
import { testimonies } from '../data/testimonies';
import { useSeo } from '../lib/useSeo';
export function Home() {
  useSeo({
    title: "Welcome to Milangela's Soul Garden",
    description: 'Find balance, release tension, and reconnect with your inner clarity through sound, vibration, and mindful presence.',
    path: '/',
    image: 'https://cdn.magicpatterns.com/uploads/rNSyYD7BhQbyjDzyPNZ74D/milangela_home.jpg'
  });
  const latestPosts = [...blogPosts].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 3);
  return <div className="min-h-screen flex flex-col bg-cream" data-id="element-276">
      <Header data-id="element-277" />

      {/* HERO */}
      <section className="relative h-screen min-h-[640px] flex items-center justify-center overflow-hidden" data-id="element-278">
        <div className="absolute inset-0 z-0" data-id="element-279">
          <img src="https://cdn.magicpatterns.com/uploads/rNSyYD7BhQbyjDzyPNZ74D/milangela_home.jpg" alt="Bench overlooking misty mountains at sunrise" className="w-full h-full object-cover" data-id="element-280" />
          <div className="absolute inset-0 bg-charcoal/20" data-id="element-281"></div>
        </div>

        <div className="relative z-10 text-center px-6 -mt-16 md:-mt-24" data-id="element-282">
          <h1 className="font-serif text-white drop-shadow-md" data-id="element-283">
            <span className="block text-5xl md:text-7xl lg:text-8xl xl:text-9xl mb-2 lg:mb-4 leading-none" data-id="element-284">
              Welcome to
            </span>
            <span className="block text-3xl md:text-5xl lg:text-6xl xl:text-7xl font-light italic leading-tight" data-id="element-285">
              Milangela's Soul Garden!
            </span>
          </h1>
        </div>

        <div className="absolute bottom-0 left-0 right-0 z-20 pointer-events-none" data-id="element-286">
          <WavyDivider fill="#F8F2E4" data-id="element-287" />
        </div>
      </section>

      {/* INTRO / WORK WITH ME */}
      <section className="bg-cream py-20 px-6" data-id="element-288">
        <div className="max-w-3xl mx-auto text-center" data-id="element-289">
          {/* Circular portrait */}
          <div className="relative w-64 h-64 md:w-80 md:h-80 mx-auto mb-12" data-id="element-290">
            <div className="absolute inset-0 rounded-full overflow-hidden shadow-soft" data-id="element-291">
              <img src="https://cdn.magicpatterns.com/uploads/9mxYgsLUEBMjuUkyXDiNhQ/milangela-circle-homepage.jpg" alt="A woman in nature among flowers" loading="lazy" className="w-full h-full object-cover" data-id="element-292" />
            </div>
          </div>

          <h2 className="font-serif text-3xl md:text-4xl text-charcoal mb-6 leading-tight" data-id="element-293">
            Where Sound, Soul · <br className="hidden md:block" data-id="element-294" />
            and Stillness Meet
          </h2>
          <p className="text-charcoal-50 font-light leading-relaxed mb-10 max-w-xl mx-auto" data-id="element-295">
            I am Milangela, a guide, teacher, and sound practitioner. In my work
            I combine sound, vibration, meditation, and supportive dialogue to
            bring body, mind, and spirit into harmony.
          </p>
          <Link to="/milangela" className="inline-block px-8 py-3 border border-charcoal text-charcoal text-sm tracking-widest uppercase hover:bg-charcoal hover:text-cream transition-colors" data-id="element-296">
            Read My Story
          </Link>
        </div>
      </section>

      {/* WAVY DIVIDER → ROSE SECTION */}
      <div className="relative bg-cream" data-id="element-297">
        <WavyDivider fill="#F5EBEB" data-id="element-298" />
      </div>

      {/* LATEST BLOG POSTS (rose section) */}
      <section className="bg-rose-50 py-20 px-6" data-id="element-299">
        <div className="max-w-7xl mx-auto" data-id="element-300">
          <h2 className="font-serif text-3xl md:text-4xl text-charcoal text-center mb-14" data-id="element-301">
            My latest <span className="text-rose-700 italic" data-id="element-302">blog posts</span>:
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-303">
            {latestPosts.map(post => <BlogCard key={post.slug} post={post} data-id="element-304" />)}
          </div>
          <div className="text-center" data-id="element-305">
            <Link to="/blog" className="inline-flex items-center text-sage-700 hover:text-sage-600 transition-colors text-sm tracking-widest uppercase group" data-id="element-306">
              See all reflections
              <ArrowRight size={16} strokeWidth={1.5} className="ml-2 transform group-hover:translate-x-1 transition-transform" data-id="element-307" />
            </Link>
          </div>
        </div>
      </section>

      {/* YOUTUBE SECTION */}
      <section className="bg-rose-50 pb-20 px-6" data-id="element-308">
        <div className="max-w-5xl mx-auto" data-id="element-309">
          <h2 className="font-serif text-3xl md:text-4xl text-charcoal text-center mb-14" data-id="element-310">
            Milangela on <span className="text-rose-700 italic" data-id="element-311">YouTube</span>:
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-312">
            {[{
            title: 'Welcome to my first live meditation',
            thumb: 'https://img.youtube.com/vi/Vgrf2WGh3UA/maxresdefault.jpg',
            href: 'https://www.youtube.com/watch?v=Vgrf2WGh3UA'
          }, {
            title: 'Inner Calm & Clarity with Holly E. Copeland',
            thumb: 'https://img.youtube.com/vi/SPK6Etji9O8/maxresdefault.jpg',
            href: 'https://www.youtube.com/watch?v=SPK6Etji9O8'
          }].map((video, i) => <a key={i} href={video.href} target="_blank" rel="noopener noreferrer" className="group relative block aspect-video rounded-2xl overflow-hidden shadow-soft bg-charcoal" data-id="element-313">
                <img src={video.thumb} alt={video.title} loading="lazy" className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" data-id="element-314" />
                <div className="absolute inset-0 bg-charcoal/30 flex items-center justify-center" data-id="element-315">
                  <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform" data-id="element-316">
                    <Play size={26} fill="#3A3A38" className="text-charcoal ml-1" data-id="element-317" />
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black/70 to-transparent" data-id="element-318">
                  <p className="text-white text-sm font-medium tracking-wide drop-shadow" data-id="element-319">
                    {video.title}
                  </p>
                </div>
              </a>)}
          </div>
        </div>
      </section>

      {/* QUOTE SECTION */}
      <section className="relative bg-rose-100 py-24 px-6 overflow-hidden" data-id="element-320">
        <div className="max-w-3xl mx-auto text-center relative z-10" data-id="element-321">
          <div className="text-6xl font-serif text-rose-600 leading-none mb-4" data-id="element-322">
            "
          </div>
          <p className="font-serif text-3xl md:text-4xl text-charcoal italic leading-snug mb-5" data-id="element-323">
            Quiet the mind, and the soul will speak.
          </p>
          <p className="text-charcoal-50 text-sm tracking-widest uppercase" data-id="element-324">
            — Ma Jaya Sati Bhagavati
          </p>
        </div>
      </section>

      {/* TESTIMONIES SECTION */}
      <section className="relative bg-rose-50 py-20 px-6" data-id="element-325">
        <div className="max-w-7xl mx-auto" data-id="element-326">
          <div className="text-center mb-14" data-id="element-327">
            <p className="text-xs tracking-[0.3em] uppercase text-sage-700 mb-3" data-id="element-328">
              In their own words
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-charcoal" data-id="element-329">
              What people share with me
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-330">
            {testimonies.map(t => <TestimonialCard key={t.id} testimony={t} data-id="element-331" />)}
          </div>
          <div className="text-center" data-id="element-332">
            <Link to="/testimonies" className="inline-flex items-center text-sage-700 hover:text-sage-600 transition-colors text-sm tracking-widest uppercase group" data-id="element-333">
              Read all testimonies
              <ArrowRight size={16} strokeWidth={1.5} className="ml-2 transform group-hover:translate-x-1 transition-transform" data-id="element-334" />
            </Link>
          </div>
        </div>
      </section>

      {/* WAVY DIVIDER INTO FOOTER */}
      <div className="relative bg-rose-50" data-id="element-335">
        <WavyDivider fill="#3A3A38" data-id="element-336" />
      </div>

      <Footer data-id="element-337" />
    </div>;
}